import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import PendingIcon from '@mui/icons-material/Pending';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadIcon from '@mui/icons-material/Upload';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    DialogContent,
    FormControl,
    FormControlLabel,
    IconButton,
    LinearProgress,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useDialog } from 'muibox';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { OnlyFansSubscriber } from '../../hooks/useSubscriber';
import { createImageThumbnail, createVideoThumbnail } from '../../utils/mediaThumbnails';
import { resolveUploadMimeType } from '../../utils/uploadTypes';
import { calculateVaultMediaHash } from '../../utils/vaultMediaHash';
import DialogAdaptive from '../common/DialogAdaptive';
import OnlyFansTagCreatorsSelector from '../common/OnlyFansTagCreatorsSelector';
import StyledCardTitleBar from '../common/StyledCardTitleBar';
import StyledDialogActions from '../common/StyledDialogActions';
import VaultUploadSettingsDialog from './VaultUploadSettings';

export const MAX_VAULT_UPLOAD_FILES = 40;

// Hash at most this many files at once. Each hash spins up a WASM worker, so an
// unbounded fan-out (up to MAX_VAULT_UPLOAD_FILES) would swamp low-end mobiles.
const HASH_CHECK_CONCURRENCY = 3;

// Decode at most this many files at once for thumbnails. Each decode holds a
// full-resolution bitmap (a 48MP photo is ~190 MB) until it is downscaled, so
// decoding the whole queue concurrently would exceed Safari's tab memory budget.
const THUMBNAIL_CONCURRENCY = 2;

interface TaskLimiter {
    active: number;
    queue: Array<() => void>;
}

// Runs at most `concurrency` tasks at a time and queues the rest.
const runLimited = (limiter: TaskLimiter, concurrency: number, task: () => Promise<void>) => {
    const run = () => {
        limiter.active += 1;
        task().finally(() => {
            limiter.active -= 1;
            const next = limiter.queue.shift();
            if (next) {
                next();
            }
        });
    };

    if (limiter.active < concurrency) {
        run();
    } else {
        limiter.queue.push(run);
    }
};

export interface UploadFileMetadata {
    file: File;
    name: string;
    description: string;
    taggedCreators: { id: number; name: string }[];
    taggedReleaseForms: { id: number; name: string }[];
}

interface VaultUploadDialogProps {
    subscriber: OnlyFansSubscriber;
    open: boolean;
    files: File[];
    onClose: () => void;
    onUpload: (filesWithMetadata: UploadFileMetadata[], albumId: number) => void;
    onAddMore: () => void;
    onRemove: (file: File) => void;
    onFilesDropped: (files: File[]) => void;
    onCheckFileExists?: (hash: string, size: number) => Promise<boolean>;
    uploading: boolean;
    progress: number;
    uploadState: string;
    eta: string | null;
    uploadSpeed: string;
    // Album selection props
    sortedAlbums?: Array<{ id: number; name: string }>;
    isLoadingAlbums?: boolean;
    onAbort: () => void;
    currentFileName?: string;
    currentFileIndex?: number;
    totalFiles?: number;
}

interface FileMetadata {
    name: string;
    description: string;
    taggedCreators: { id: number; name: string }[];
    taggedReleaseForms: { id: number; name: string }[];
}

interface HashCheckStatus {
    // checking: hashing / awaiting the vault lookup
    // duplicate: the file already exists in the vault and must be removed before uploading
    // ok: the file is new and safe to upload
    // error: the check could not be completed (non-blocking)
    state: 'checking' | 'duplicate' | 'ok' | 'error';
}

export default function VaultUploadDialog({
    subscriber,
    open,
    files,
    onClose,
    onUpload,
    onAddMore,
    onRemove,
    onFilesDropped,
    onCheckFileExists,
    uploading,
    progress,
    uploadState,
    eta,
    uploadSpeed,
    sortedAlbums = [],
    isLoadingAlbums = false,
    onAbort,
    currentFileName,
    currentFileIndex = 0,
    totalFiles,
}: VaultUploadDialogProps) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const dialog = useDialog();

    const [showSettings, setShowSettings] = useState(false);
    const [metadata, setMetadata] = useState<Map<string, FileMetadata>>(new Map());
    const [thumbnails, setThumbnails] = useState<Map<string, string>>(new Map());
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [selectedAlbum, setSelectedAlbum] = useState<number>(0);

    // Tag creators dialog state
    const [openTagCreators, setOpenTagCreators] = useState(false);
    const [currentTaggingFile, setCurrentTaggingFile] = useState<string | null>(null);
    const [tempTaggedCreators, setTempTaggedCreators] = useState<{ id: number; name: string }[]>([]);
    const [tempTaggedReleaseForms, setTempTaggedReleaseForms] = useState<{ id: number; name: string }[]>([]);

    // Bulk tagging state
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
    const [bulkTagging, setBulkTagging] = useState(false);

    // Per-file duplicate-detection state, keyed by file name (matches metadata/thumbnails).
    // Before uploading, each file is hashed (S3-ETag style) and checked against the vault so
    // already-existing files can be flagged and blocked without wasting an upload.
    const [hashChecks, setHashChecks] = useState<Map<string, HashCheckStatus>>(new Map());
    const startedHashChecksRef = useRef<Set<string>>(new Set());

    // Per-purpose concurrency limiters shared for the component's lifetime.
    const hashQueueRef = useRef<TaskLimiter>({ active: 0, queue: [] });
    const thumbnailQueueRef = useRef<TaskLimiter>({ active: 0, queue: [] });

    const scheduleHashCheck = useCallback((task: () => Promise<void>) => {
        runLimited(hashQueueRef.current, HASH_CHECK_CONCURRENCY, task);
    }, []);

    const scheduleThumbnail = useCallback((task: () => Promise<void>) => {
        runLimited(thumbnailQueueRef.current, THUMBNAIL_CONCURRENCY, task);
    }, []);

    // Which files have had thumbnail generation started, and the latest generated
    // URLs (mirrors the `thumbnails` state so cleanup paths can revoke object URLs
    // without depending on render timing).
    const startedThumbnailsRef = useRef<Set<string>>(new Set());
    const thumbnailsRef = useRef<Map<string, string>>(new Map());

    // Reset duplicate-detection state whenever the dialog is closed so it re-checks on reopen.
    useEffect(() => {
        if (!open) {
            startedHashChecksRef.current = new Set();
            setHashChecks(new Map());
        }
    }, [open]);

    // Hash each queued file and check whether it already exists in the vault.
    useEffect(() => {
        if (!open || !onCheckFileExists) {
            return;
        }

        const currentNames = new Set(files.map(file => file.name));

        // Drop tracking for files that were removed from the queue.
        startedHashChecksRef.current.forEach(name => {
            if (!currentNames.has(name)) {
                startedHashChecksRef.current.delete(name);
            }
        });
        setHashChecks(prev => {
            if (![...prev.keys()].some(name => !currentNames.has(name))) {
                return prev;
            }
            const next = new Map(prev);
            next.forEach((_, name) => {
                if (!currentNames.has(name)) {
                    next.delete(name);
                }
            });
            return next;
        });

        // Kick off a check for any file we haven't started yet.
        files.forEach(file => {
            if (startedHashChecksRef.current.has(file.name)) {
                return;
            }
            startedHashChecksRef.current.add(file.name);

            // Queued files show as "checking" until a slot frees up, so the upload
            // button stays disabled while any file is still pending verification.
            setHashChecks(prev => new Map(prev).set(file.name, { state: 'checking' }));

            scheduleHashCheck(async () => {
                // The file may have been removed while queued.
                if (!startedHashChecksRef.current.has(file.name)) {
                    return;
                }

                try {
                    const hash = await calculateVaultMediaHash(file);
                    const exists = await onCheckFileExists(hash, file.size);
                    setHashChecks(prev => {
                        // The file may have been removed while the check was running.
                        if (!startedHashChecksRef.current.has(file.name)) {
                            return prev;
                        }
                        return new Map(prev).set(file.name, exists ? { state: 'duplicate' } : { state: 'ok' });
                    });
                } catch (error) {
                    console.error('Vault duplicate check failed', error);
                    setHashChecks(prev => {
                        if (!startedHashChecksRef.current.has(file.name)) {
                            return prev;
                        }
                        return new Map(prev).set(file.name, { state: 'error' });
                    });
                }
            });
        });
    }, [open, files, onCheckFileExists, scheduleHashCheck]);

    const hasDuplicateFiles = files.some(file => hashChecks.get(file.name)?.state === 'duplicate');
    const isCheckingHashes = files.some(file => hashChecks.get(file.name)?.state === 'checking');

    useEffect(() => {
        if (!open) {
            return;
        }

        setMetadata(prev => {
            const newMetadata = new Map(prev);
            const fileNames = new Set(files.map(file => file.name));

            for (const fileName of newMetadata.keys()) {
                if (!fileNames.has(fileName)) {
                    newMetadata.delete(fileName);
                }
            }

            files.forEach(file => {
                if (!newMetadata.has(file.name)) {
                    const defaultName = file.name.replace(/\.[^/.]+$/, '');
                    newMetadata.set(file.name, {
                        name: defaultName,
                        description: '',
                        taggedCreators: [],
                        taggedReleaseForms: [],
                    });
                }
            });

            return newMetadata;
        });

        setSelectedFiles(prev => {
            const fileNames = new Set(files.map(file => file.name));
            const newSelection = new Set([...prev].filter(fileName => fileNames.has(fileName)));
            return newSelection.size === prev.size ? prev : newSelection;
        });
    }, [open, files]);

    // Generate small downscaled thumbnails for image and video files, incrementally:
    // only newly added files are decoded (removals just revoke their URLs), and decodes
    // run through a concurrency limiter so a queue of large photos can't exhaust tab
    // memory — handing full files straight to <img> used to crash Safari with HEICs.
    useEffect(() => {
        if (!open) {
            thumbnailsRef.current.forEach(url => URL.revokeObjectURL(url));
            thumbnailsRef.current = new Map();
            startedThumbnailsRef.current = new Set();
            setThumbnails(new Map());
            return;
        }

        const currentNames = new Set(files.map(file => file.name));

        startedThumbnailsRef.current.forEach(name => {
            if (!currentNames.has(name)) {
                startedThumbnailsRef.current.delete(name);
            }
        });

        let removedAny = false;
        thumbnailsRef.current.forEach((url, name) => {
            if (!currentNames.has(name)) {
                URL.revokeObjectURL(url);
                thumbnailsRef.current.delete(name);
                removedAny = true;
            }
        });
        if (removedAny) {
            setThumbnails(new Map(thumbnailsRef.current));
        }

        files.forEach(file => {
            if (startedThumbnailsRef.current.has(file.name)) {
                return;
            }

            const mimeType = resolveUploadMimeType(file);
            const isVideo = mimeType.startsWith('video/');
            // Images include HEIC: Safari decodes it natively for a real preview;
            // browsers that can't decode it resolve null and fall back to the icon.
            const isImage = mimeType.startsWith('image/');
            if (!isVideo && !isImage) {
                return;
            }

            startedThumbnailsRef.current.add(file.name);

            scheduleThumbnail(async () => {
                // The file may have been removed while queued.
                if (!startedThumbnailsRef.current.has(file.name)) {
                    return;
                }

                const url = isVideo ? await createVideoThumbnail(file) : await createImageThumbnail(file);
                if (!url) {
                    return;
                }

                if (!startedThumbnailsRef.current.has(file.name)) {
                    URL.revokeObjectURL(url);
                    return;
                }

                thumbnailsRef.current.set(file.name, url);
                setThumbnails(new Map(thumbnailsRef.current));
            });
        });
    }, [open, files, scheduleThumbnail]);

    // Revoke any remaining thumbnail URLs on unmount.
    useEffect(() => {
        const thumbnailUrls = thumbnailsRef.current;
        return () => {
            thumbnailUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    const handleMetadataChange = (fileName: string, field: 'name' | 'description', value: string) => {
        setMetadata(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(fileName) || {
                name: '',
                description: '',
                taggedCreators: [],
                taggedReleaseForms: [],
            };
            newMap.set(fileName, { ...current, [field]: value });
            return newMap;
        });
    };

    const handleAlbumChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSelectedAlbum(parseInt(event.target.value, 10));
    };

    const handleRemoveFile = (file: File) => {
        // Thumbnail cleanup happens in the thumbnail effect when the files prop updates.
        setMetadata(prev => {
            const newMap = new Map(prev);
            newMap.delete(file.name);
            return newMap;
        });

        setSelectedFiles(prev => {
            if (!prev.has(file.name)) {
                return prev;
            }
            const newSet = new Set(prev);
            newSet.delete(file.name);
            return newSet;
        });

        onRemove(file);
    };

    const handleToggleFileSelection = (fileName: string) => {
        setSelectedFiles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fileName)) {
                newSet.delete(fileName);
            } else {
                newSet.add(fileName);
            }
            return newSet;
        });
    };

    const handleToggleSelectAll = () => {
        setSelectedFiles(prev => (prev.size === files.length ? new Set() : new Set(files.map(file => file.name))));
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget === e.target) {
            setIsDraggingOver(false);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);

        if (files.length >= MAX_VAULT_UPLOAD_FILES) {
            return;
        }

        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) {
            onFilesDropped(droppedFiles);
        }
    };

    const handleOpenTagCreators = (fileName: string) => {
        const fileMetadata = metadata.get(fileName);
        setCurrentTaggingFile(fileName);
        setBulkTagging(false);
        setTempTaggedCreators(fileMetadata?.taggedCreators || []);
        setTempTaggedReleaseForms(fileMetadata?.taggedReleaseForms || []);
        setOpenTagCreators(true);
    };

    const handleOpenBulkTagCreators = () => {
        setCurrentTaggingFile(null);
        setBulkTagging(true);
        setTempTaggedCreators([]);
        setTempTaggedReleaseForms([]);
        setOpenTagCreators(true);
    };

    const mergeTagsById = (existing: { id: number; name: string }[], added: { id: number; name: string }[]) => {
        const existingIds = new Set(existing.map(item => item.id));
        return [...existing, ...added.filter(item => !existingIds.has(item.id))];
    };

    const handleCloseTagCreators = () => {
        if (bulkTagging) {
            // Merge the chosen tags into every selected file's metadata
            setMetadata(prev => {
                const newMap = new Map(prev);
                selectedFiles.forEach(fileName => {
                    const current = newMap.get(fileName) || {
                        name: '',
                        description: '',
                        taggedCreators: [],
                        taggedReleaseForms: [],
                    };
                    newMap.set(fileName, {
                        ...current,
                        taggedCreators: mergeTagsById(current.taggedCreators, tempTaggedCreators),
                        taggedReleaseForms: mergeTagsById(current.taggedReleaseForms, tempTaggedReleaseForms),
                    });
                });
                return newMap;
            });
            setSelectedFiles(new Set());
        } else if (currentTaggingFile) {
            // Save the tagged creators/forms to the file metadata
            setMetadata(prev => {
                const newMap = new Map(prev);
                const current = newMap.get(currentTaggingFile) || {
                    name: '',
                    description: '',
                    taggedCreators: [],
                    taggedReleaseForms: [],
                };
                newMap.set(currentTaggingFile, {
                    ...current,
                    taggedCreators: tempTaggedCreators,
                    taggedReleaseForms: tempTaggedReleaseForms,
                });
                return newMap;
            });
        }
        setOpenTagCreators(false);
        setCurrentTaggingFile(null);
        setBulkTagging(false);
        setTempTaggedCreators([]);
        setTempTaggedReleaseForms([]);
    };

    const handleRemoveTaggedCreator = (fileName: string, creatorId: number) => {
        setMetadata(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(fileName);
            if (current) {
                newMap.set(fileName, {
                    ...current,
                    taggedCreators: current.taggedCreators.filter(c => c.id !== creatorId),
                });
            }
            return newMap;
        });
    };

    const handleRemoveTaggedReleaseForm = (fileName: string, formId: number) => {
        setMetadata(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(fileName);
            if (current) {
                newMap.set(fileName, {
                    ...current,
                    taggedReleaseForms: current.taggedReleaseForms.filter(f => f.id !== formId),
                });
            }
            return newMap;
        });
    };

    const handleUpload = () => {
        const filesWithMetadata: UploadFileMetadata[] = files.map(file => {
            const fileMetadata = metadata.get(file.name) || {
                name: file.name,
                description: '',
                taggedCreators: [],
                taggedReleaseForms: [],
            };
            return {
                file,
                name: fileMetadata.name || file.name,
                description: fileMetadata.description || '',
                taggedCreators: fileMetadata.taggedCreators || [],
                taggedReleaseForms: fileMetadata.taggedReleaseForms || [],
            };
        });
        onUpload(filesWithMetadata, selectedAlbum);
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const renderThumbnail = (file: File) => {
        const thumbnailUrl = thumbnails.get(file.name);

        if (thumbnailUrl) {
            return (
                <Box
                    sx={{
                        width: fullScreen ? 60 : 80,
                        height: fullScreen ? 60 : 80,
                        borderRadius: 1,
                        overflow: 'hidden',
                        flexShrink: 0,
                        bgcolor: theme.palette.grey[200],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <img
                        src={thumbnailUrl}
                        alt={file.name}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                </Box>
            );
        }

        let Icon = ImageIcon;
        let iconColor = theme.palette.grey[400];

        if (file.type.startsWith('audio/')) {
            Icon = AudiotrackIcon;
            iconColor = theme.palette.primary.main;
        } else if (file.type.startsWith('video/')) {
            Icon = VideocamIcon;
            iconColor = theme.palette.secondary.main;
        }

        return (
            <Box
                sx={{
                    width: fullScreen ? 60 : 80,
                    height: fullScreen ? 60 : 80,
                    borderRadius: 1,
                    bgcolor: theme.palette.grey[200],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Icon sx={{ fontSize: fullScreen ? 32 : 40, color: iconColor }} />
            </Box>
        );
    };

    const atUploadLimit = files.length >= MAX_VAULT_UPLOAD_FILES;

    return (
        <>
            <DialogAdaptive
                open={open}
                onClose={uploading ? undefined : onClose}
                maxWidth="md"
                fullWidth
                fullScreen={fullScreen}
                disableEscapeKeyDown={uploading}
            >
                <StyledCardTitleBar title="Upload Files to Vault" isLoading={uploading} />

                <DialogContent sx={{ p: 2 }}>
                    {!subscriber.messageUsers?.uploadMediaDesignatedUser?.id && (
                        <Box mb={2}>
                            <Alert severity="warning">
                                Warning: You do not have a designated upload user set. Please click on Settings below to set one before
                                uploading media.
                            </Alert>
                        </Box>
                    )}

                    {uploading && (
                        <Box mb={1}>
                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {uploadState}
                                    </Typography>
                                </Stack>

                                {/* Overall Progress */}
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                            Overall Progress ({currentFileIndex + 1}/{totalFiles})
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {progress}%
                                        </Typography>
                                    </Stack>
                                    <LinearProgress variant="determinate" value={progress} />
                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
                                        {eta ? (
                                            <Typography variant="body2" color="text.secondary">
                                                ETA: {eta}
                                            </Typography>
                                        ) : (
                                            <Box />
                                        )}
                                        <Typography variant="body2" color="text.secondary">
                                            {uploadSpeed}
                                        </Typography>
                                    </Stack>
                                </Box>

                                {/* Current File Progress */}
                                {currentFileName && (
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            noWrap
                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                        >
                                            Current: {currentFileName}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {/* Album Selection */}
                    {!uploading && sortedAlbums.length > 0 && (
                        <Box mb={1}>
                            <FormControl fullWidth size="small">
                                <Typography variant="body2" color="text.secondary">
                                    Add to album (optional)
                                </Typography>
                                <TextField
                                    select
                                    value={selectedAlbum.toString()}
                                    onChange={handleAlbumChange}
                                    size="small"
                                    fullWidth
                                    SelectProps={{
                                        native: true,
                                    }}
                                    disabled={isLoadingAlbums}
                                >
                                    <option value="0">All (No specific album)</option>
                                    {sortedAlbums.map(album => (
                                        <option key={album.id} value={album.id}>
                                            {album.name}
                                        </option>
                                    ))}
                                </TextField>
                            </FormControl>
                        </Box>
                    )}

                    {!uploading && (
                        <Typography variant="body2" color={atUploadLimit ? 'warning.main' : 'text.secondary'} sx={{ mb: 1 }}>
                            {files.length}/{MAX_VAULT_UPLOAD_FILES} files
                            {atUploadLimit ? ' (maximum reached)' : ''}
                        </Typography>
                    )}

                    {/* Bulk selection controls */}
                    {!uploading && files.length > 0 && (
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedFiles.size === files.length && files.length > 0}
                                        indeterminate={selectedFiles.size > 0 && selectedFiles.size < files.length}
                                        onChange={handleToggleSelectAll}
                                    />
                                }
                                label={
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedFiles.size > 0 ? `${selectedFiles.size} selected` : 'Select all'}
                                    </Typography>
                                }
                            />
                            {selectedFiles.size > 0 && (
                                <Tooltip title="Tag creators and release forms on all selected files">
                                    <Button size="small" variant="outlined" onClick={handleOpenBulkTagCreators}>
                                        Tag Selected ({selectedFiles.size})
                                    </Button>
                                </Tooltip>
                            )}
                        </Stack>
                    )}

                    <Stack spacing={2}>
                        {files.map((file, index) => {
                            const fileMetadata = metadata.get(file.name);
                            const isCurrentFile = currentFileName === file.name;
                            const isCompleted = currentFileIndex > index;
                            const isUpcoming = currentFileIndex < index;
                            const hashCheck = hashChecks.get(file.name);
                            const isDuplicate = hashCheck?.state === 'duplicate';

                            return (
                                <Box
                                    key={`${file.name}-${index}`}
                                    sx={{
                                        p: 2,
                                        border: 1,
                                        borderColor: isDuplicate ? 'error.main' : isCurrentFile ? 'primary.main' : 'divider',
                                        borderRadius: 1,
                                        bgcolor: isDuplicate
                                            ? alpha(theme.palette.error.light, 0.15)
                                            : isCompleted
                                              ? alpha(theme.palette.success.light, 0.3)
                                              : theme.palette.background.default,
                                        position: 'relative',
                                        opacity: uploading && isUpcoming ? 0.6 : 1,
                                    }}
                                >
                                    {!uploading && hashCheck?.state === 'checking' && (
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                            <CircularProgress size={14} />
                                            <Typography variant="caption" color="text.secondary">
                                                Checking if this file is already in your vault…
                                            </Typography>
                                        </Stack>
                                    )}
                                    {!uploading && isDuplicate && (
                                        <Alert severity="error" sx={{ mb: 1.5 }}>
                                            This file is already in your vault. Remove it from the queue to continue.
                                        </Alert>
                                    )}
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        {/* Thumbnail */}
                                        <Stack direction="column" alignItems="center" spacing={1} mb={1}>
                                            {renderThumbnail(file)}

                                            <Tooltip title="Tag creators and release forms">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleOpenTagCreators(file.name)}
                                                    disabled={uploading}
                                                    fullWidth
                                                >
                                                    Tags
                                                </Button>
                                            </Tooltip>
                                        </Stack>
                                        {/* File info and metadata fields */}
                                        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                                {!uploading && (
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedFiles.has(file.name)}
                                                        onChange={() => handleToggleFileSelection(file.name)}
                                                        sx={{ p: 0.5 }}
                                                    />
                                                )}
                                                <Typography
                                                    variant="subtitle2"
                                                    noWrap
                                                    sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                    title={file.name}
                                                >
                                                    {file.name}
                                                </Typography>

                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatFileSize(file.size)}
                                                    </Typography>
                                                    {/* Show checkmark for completed files */}
                                                    {isCompleted && uploading && (
                                                        <CheckCircleIcon
                                                            sx={{
                                                                color: 'success.main',
                                                            }}
                                                        />
                                                    )}

                                                    {!isCompleted && uploading && (
                                                        <PendingIcon
                                                            sx={{
                                                                color: 'warning.main',
                                                            }}
                                                        />
                                                    )}

                                                    {/* Remove button - top right corner */}
                                                    {!uploading && (
                                                        <IconButton
                                                            onClick={() => handleRemoveFile(file)}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: 'background.paper',
                                                                boxShadow: 1,
                                                                '&:hover': {
                                                                    bgcolor: 'error.main',
                                                                    color: 'white',
                                                                },
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </Stack>

                                            <TextField
                                                label="Name (optional)"
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                value={fileMetadata?.name || ''}
                                                onChange={e => handleMetadataChange(file.name, 'name', e.target.value)}
                                                disabled={uploading}
                                                placeholder={file.name.replace(/\.[^/.]+$/, '')}
                                            />

                                            <TextField
                                                label="Description (optional)"
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={fileMetadata?.description || ''}
                                                onChange={e => handleMetadataChange(file.name, 'description', e.target.value)}
                                                disabled={uploading}
                                                placeholder="Add a description..."
                                            />

                                            {/* Tag Creators Button and Chips */}
                                            {/* Display Tagged Creators */}
                                            {fileMetadata?.taggedCreators && fileMetadata.taggedCreators.length > 0 && (
                                                <Box mb={0.5}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                        Tagged Creators:
                                                    </Typography>
                                                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                        {fileMetadata.taggedCreators.map(creator => (
                                                            <Chip
                                                                key={creator.id}
                                                                label={creator.name}
                                                                size="small"
                                                                onDelete={
                                                                    uploading
                                                                        ? undefined
                                                                        : () => handleRemoveTaggedCreator(file.name, creator.id)
                                                                }
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            )}

                                            {/* Display Tagged Release Forms */}
                                            {fileMetadata?.taggedReleaseForms && fileMetadata.taggedReleaseForms.length > 0 && (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                        Tagged Release Forms:
                                                    </Typography>
                                                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                        {fileMetadata.taggedReleaseForms.map(form => (
                                                            <Chip
                                                                key={form.id}
                                                                label={form.name}
                                                                size="small"
                                                                onDelete={
                                                                    uploading
                                                                        ? undefined
                                                                        : () => handleRemoveTaggedReleaseForm(file.name, form.id)
                                                                }
                                                                color="secondary"
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Box>
                            );
                        })}

                        {/* Add more files button with drag and drop */}
                        {!uploading && !atUploadLimit && (
                            <Box
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                sx={{
                                    position: 'relative',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    startIcon={<AddPhotoAlternateIcon />}
                                    onClick={onAddMore}
                                    fullWidth
                                    sx={{
                                        py: 2,
                                        borderStyle: 'dashed',
                                        borderWidth: 2,
                                        borderColor: isDraggingOver ? 'primary.main' : 'divider',
                                        bgcolor: isDraggingOver ? 'action.hover' : 'transparent',
                                        transform: isDraggingOver ? 'scale(1.02)' : 'scale(1)',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            borderStyle: 'dashed',
                                            borderWidth: 2,
                                            bgcolor: 'action.hover',
                                        },
                                    }}
                                >
                                    {isDraggingOver ? 'Drop files here to add' : 'Add More Files'}
                                </Button>
                                {isDraggingOver && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            pointerEvents: 'none',
                                            borderRadius: 1,
                                            bgcolor: 'primary.main',
                                            opacity: 0.05,
                                        }}
                                    />
                                )}
                            </Box>
                        )}
                    </Stack>
                </DialogContent>

                <StyledDialogActions>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" width="100%">
                        <Button
                            onClick={() => setShowSettings(prev => !prev)}
                            startIcon={<SettingsIcon />}
                            disabled={uploading}
                            color="secondary"
                        >
                            Settings
                        </Button>
                        <VaultUploadSettingsDialog subscriber={subscriber} open={showSettings} handleClose={() => setShowSettings(false)} />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                color={uploading || files.length > 0 ? 'error' : 'primary'}
                                onClick={() => {
                                    if (uploading) {
                                        dialog
                                            .confirm({
                                                title: 'Cancel Upload',
                                                message: 'Are you sure you want to cancel the upload? All progress will be lost.',
                                                ok: {
                                                    text: 'Cancel Upload',
                                                    color: 'error',
                                                    variant: 'contained',
                                                },
                                                cancel: {
                                                    text: 'Continue Upload',
                                                    color: 'primary',
                                                    variant: 'outlined',
                                                },
                                            })
                                            .then(() => {
                                                onAbort();
                                                onClose();
                                            })
                                            .catch(() => {});

                                        return;
                                    }

                                    if (files.length > 0) {
                                        dialog
                                            .confirm({
                                                title: 'Discard Changes',
                                                message:
                                                    'Are you sure you want to close the upload dialog? All selected files will be lost.',
                                                ok: {
                                                    text: 'Discard',
                                                    color: 'error',
                                                    variant: 'contained',
                                                },
                                                cancel: {
                                                    text: 'Keep Uploading',
                                                    color: 'primary',
                                                    variant: 'outlined',
                                                },
                                            })
                                            .then(() => {
                                                onClose();
                                            })
                                            .catch(() => {});

                                        return;
                                    }

                                    onClose();
                                }}
                                startIcon={<CloseIcon />}
                            >
                                {uploading ? 'Cancel Upload' : files.length > 0 ? 'Discard' : 'Close'}
                            </Button>
                            <Tooltip
                                title={
                                    hasDuplicateFiles
                                        ? 'Remove the files already in your vault before uploading'
                                        : isCheckingHashes
                                          ? 'Checking your files against the vault…'
                                          : ''
                                }
                            >
                                <span>
                                    <Button
                                        onClick={handleUpload}
                                        variant="contained"
                                        disabled={
                                            uploading ||
                                            files.length === 0 ||
                                            hasDuplicateFiles ||
                                            isCheckingHashes ||
                                            !subscriber.messageUsers?.uploadMediaDesignatedUser?.id
                                        }
                                        startIcon={<UploadIcon />}
                                    >
                                        Upload {files.length > 1 ? `${files.length} Files` : 'File'}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </StyledDialogActions>
            </DialogAdaptive>

            {/* Tag Creators Dialog */}
            <DialogAdaptive open={openTagCreators} onClose={handleCloseTagCreators}>
                <StyledCardTitleBar title={bulkTagging ? `Tag ${selectedFiles.size} Selected Files` : 'Tag Creators'} />

                <Box sx={{ p: 2 }}>
                    {bulkTagging && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            The creators and release forms you choose will be added to all {selectedFiles.size} selected files, on top of
                            any tags they already have.
                        </Alert>
                    )}
                    <OnlyFansTagCreatorsSelector
                        subscriber={subscriber}
                        tagCreators={true}
                        setTagCreators={() => {}}
                        taggedCreators={tempTaggedCreators}
                        setTaggedCreators={setTempTaggedCreators}
                        taggedReleaseForms={tempTaggedReleaseForms}
                        setTaggedReleaseForms={setTempTaggedReleaseForms}
                        session={null}
                        disabled={false}
                    />
                </Box>

                <StyledDialogActions>
                    <Button onClick={handleCloseTagCreators} variant="contained" color="primary">
                        Done
                    </Button>
                </StyledDialogActions>
            </DialogAdaptive>
        </>
    );
}
